<template>
  <v-container fluid v-if="loadedSettings">
    <v-form>
      <v-card outlined class="mb-5">
        <v-card-title style="word-break: normal !important">
          {{ $t('location.globalSpecificClosures') }}
        </v-card-title>
        <v-container fluid>
          <v-data-table
            dense
            :headers="headers"
            :items="loadedSettings.globalSpecialClosures"
            :loading-text="$t('location.specialClosureLoadingText')"
            :no-data-text="$t('location.noSpecialClosure')"
            mobile-breakpoint="768"
            :footer-props="footerProps"
            :options.sync="options"
            :loading="loading"
          >
            <template v-slot:top>
              <v-toolbar flat>
                <p>{{ $t('location.specialClosuresByLocations') }}</p>
                <v-spacer></v-spacer>
                <v-btn color="primary" outlined @click="openAddSpecialClosureModal()">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </v-toolbar>
            </template>
            <template v-slot:item.locations="{ item }">
              <p class="pt-3">
                {{ getLocationsText(item.locations) }}
              </p>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon class="pl-0 pr-0" @click="editSelectedSpecialClosure(item)">
                <v-icon color="primary">mdi-square-edit-outline</v-icon>
              </v-btn>
              <v-btn icon class="pl-0 pr-0" @click="openDeleteSelectedSpecialClosureDialog(item)">
                <v-icon color="error">mdi-delete</v-icon>
              </v-btn>
            </template>
            <template v-slot:item.begin="{ item }">
              {{ item.begin | moment('L LT') }}
            </template>
            <template v-slot:item.end="{ item }">
              {{ item.end | moment('L LT') }}
            </template>
          </v-data-table>
          <confirmation-dialog
            v-model="openDeleteItemModal"
            :text="$t('location.deleteSpecialClosure')"
            :title="$t('location.deleteSpecialClosureTitle')"
            @no="openDeleteItemModal = false"
            @yes="deleteItem(selectedSpecialClosure)"
          />
          <location-global-special-closures-dialog
            v-if="addSpecialClosures"
            v-model="addSpecialClosures"
            :locations="locations"
            :title="$t('location.addSpecialClosure')"
            @close="closeAddSpecialClosure"
            @confirm="addSpecialClosure"
          />
          <location-global-special-closures-dialog
            v-if="editSpecialClosureModal"
            v-model="editSpecialClosureModal"
            :temporaryClosure="selectedSpecialClosure"
            :locations="locations"
            :title="$t('location.modifySpecialClosure')"
            @close="closeEditSelectedSpecialClosure"
            @confirm="updateSpecialClosure"
          />
        </v-container>
      </v-card>
    </v-form>
  </v-container>
</template>

<script>
  import confirmationDialog from '@/components/ConfirmationDialog'
  import LocationGlobalSpecialClosuresDialog from '@/components/dialogs/LocationGlobalSpecialClosuresDialog.vue'
  import { LocationService } from '@/services/locationService'

  export default {
    name: 'GlobalSpecialClosures',
    components: {
      confirmationDialog,
      LocationGlobalSpecialClosuresDialog,
    },
    props: {
      loadedSettings: {
        type: Object,
        required: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        locations: [],
        options: {
          itemsPerPage: 15,
        },
        openDeleteItemModal: false,
        selectedSpecialClosure: {},
        editSpecialClosureModal: false,
        addSpecialClosures: false,
      }
    },
    computed: {
      footerProps() {
        return {
          'items-per-page-options': [1, 5, 15, 25, 50],
          'items-per-page-text': this.$t('location.specialClosuresPerPage'),
        }
      },
      headers() {
        return [
          {
            text: this.$t('location.begin'),
            value: 'begin',
          },
          {
            text: this.$t('location.end'),
            value: 'end',
          },
          {
            text: this.$t('location.locations'),
            value: 'locations',
          },
          {
            text: '',
            value: 'actions',
            sortable: false,
          },
        ]
      },
    },
    async mounted() {
      const queryStringParameters = {
        itemsPerPage: false,
        fields: ['location_id', 'name'],
      }
      const res = await LocationService.getLocations(this.$auth.accessToken, queryStringParameters)
      this.locations = res?.data ?? this.locations
    },
    methods: {
      deleteItem() {
        this.loadedSettings.globalSpecialClosures.splice(
          this.loadedSettings.globalSpecialClosures.indexOf(this.selectedSpecialClosure),
          1
        )
        this.openDeleteItemModal = false
        this.selectedSpecialClosure = {}
      },
      openDeleteSelectedSpecialClosureDialog(item) {
        this.selectedSpecialClosure = item
        this.openDeleteItemModal = true
      },
      editSelectedSpecialClosure(item) {
        this.selectedSpecialClosure = item
        this.editSpecialClosureModal = true
      },
      closeEditSelectedSpecialClosure() {
        this.editSpecialClosureModal = false
        this.selectedSpecialClosure = {}
      },
      updateSpecialClosure(updatedSpecialClosure) {
        this.selectedSpecialClosure.locations = updatedSpecialClosure.locations
        this.selectedSpecialClosure.begin = updatedSpecialClosure.begin
        this.selectedSpecialClosure.end = updatedSpecialClosure.end
        this.closeEditSelectedSpecialClosure()
      },
      openAddSpecialClosureModal() {
        this.selectedSpecialClosure = { begin: undefined, end: undefined, locations: [] }
        this.addSpecialClosures = true
      },
      closeAddSpecialClosure() {
        this.addSpecialClosures = false
        this.selectedSpecialClosure = {}
      },
      addSpecialClosure(newMaxAllocations) {
        this.loadedSettings.globalSpecialClosures.unshift(newMaxAllocations)
        this.closeAddSpecialClosure()
      },
      getLocationTextValue(id) {
        const location = this.locations.find((location) => location.location_id === id)
        if (!location) {
          return id
        }
        return `${location.location_id} - ${location.name}`
      },
      getLocationsText(locations) {
        if (!Array.isArray(locations) || locations.length === 0) {
          return
        }
        if (locations.length === this.locations.length) {
          return this.$t('settings.all')
        }
        const length = locations.length - 1
        return this.$tc('location.locationIdNamePlusOther', length, { length, name: this.getLocationTextValue(locations[0]) })
      },
    },
  }
</script>
